/* =====================================================
   EIGHTEEN WELLNESS TECH — 360° Product Viewer
   ===================================================== */

class Viewer360 {
  constructor(options = {}) {
    this.images = options.images || [];
    this.container = options.container;
    this.autoplay = options.autoplay !== false;
    this.autoplaySpeed = options.autoplaySpeed || 80;
    this.sensitivity = options.sensitivity || 1.2;

    this.currentIndex = 0;
    this.isDragging = false;
    this.lastX = 0;
    this.autoplayTimer = null;
    this.loaded = 0;
    this.imageEls = [];

    if (!this.container || this.images.length === 0) return;
    this.init();
  }

  async init() {
    this.renderUI();
    await this.preloadImages();
    this.bindEvents();
    if (this.autoplay) this.startAutoplay();
  }

  renderUI() {
    this.container.innerHTML = `
      <div class="v360-wrap" style="position:relative;width:100%;height:100%;overflow:hidden;cursor:grab;user-select:none;">
        <canvas class="v360-canvas" style="width:100%;height:100%;display:block;"></canvas>
        <div class="v360-loader" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(250,250,248,0.9);gap:16px;">
          <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#6B6B6B;">Loading 360°</div>
          <div style="width:120px;height:2px;background:#E8E8E6;border-radius:99px;overflow:hidden;">
            <div class="v360-progress" style="height:100%;background:#C8A882;width:0%;transition:width 0.3s;"></div>
          </div>
        </div>
        <div class="v360-controls" style="position:absolute;bottom:16px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:12px;opacity:0;transition:opacity 0.3s;">
          <button class="v360-play" style="width:36px;height:36px;border-radius:50%;background:rgba(10,10,10,0.7);color:#fff;display:flex;align-items:center;justify-content:center;border:none;cursor:pointer;" title="Auto-rotate">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <div style="font-size:10px;letter-spacing:0.2em;text-transform:uppercase;color:rgba(255,255,255,0.7);background:rgba(10,10,10,0.5);padding:6px 12px;border-radius:99px;">
            Drag to rotate
          </div>
        </div>
      </div>`;

    this.wrap = this.container.querySelector('.v360-wrap');
    this.canvas = this.container.querySelector('.v360-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.loader = this.container.querySelector('.v360-loader');
    this.progress = this.container.querySelector('.v360-progress');
    this.controls = this.container.querySelector('.v360-controls');
    this.playBtn = this.container.querySelector('.v360-play');

    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    this.container.addEventListener('mouseenter', () => { this.controls.style.opacity = '1'; });
    this.container.addEventListener('mouseleave', () => { this.controls.style.opacity = '0'; });
    this.playBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.autoplayTimer) { this.stopAutoplay(); this.playBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'; }
      else { this.startAutoplay(); this.playBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>'; }
    });
  }

  resizeCanvas() {
    if (!this.canvas) return;
    const rect = this.container.getBoundingClientRect();
    this.canvas.width = rect.width * window.devicePixelRatio;
    this.canvas.height = rect.height * window.devicePixelRatio;
    this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    this.drawFrame();
  }

  preloadImages() {
    return new Promise(resolve => {
      let loaded = 0;
      const total = this.images.length;
      if (total === 0) { resolve(); return; }

      this.images.forEach((src, i) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this.imageEls[i] = img;
          loaded++;
          const pct = (loaded / total) * 100;
          if (this.progress) this.progress.style.width = `${pct}%`;
          if (loaded === total) {
            this.loader.style.opacity = '0';
            setTimeout(() => { this.loader.style.display = 'none'; }, 300);
            this.drawFrame();
            resolve();
          }
        };
        img.onerror = () => {
          loaded++;
          if (loaded === total) resolve();
        };
        img.src = src;
      });
    });
  }

  drawFrame() {
    if (!this.ctx || !this.imageEls[this.currentIndex]) return;
    const img = this.imageEls[this.currentIndex];
    const rect = this.container.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    this.ctx.clearRect(0, 0, w, h);

    const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    const dx = (w - dw) / 2;
    const dy = (h - dh) / 2;
    this.ctx.drawImage(img, dx, dy, dw, dh);
  }

  bindEvents() {
    this.wrap.addEventListener('mousedown', e => this.onDragStart(e.clientX));
    window.addEventListener('mousemove', e => this.onDragMove(e.clientX));
    window.addEventListener('mouseup', () => this.onDragEnd());
    this.wrap.addEventListener('touchstart', e => this.onDragStart(e.touches[0].clientX), { passive: true });
    window.addEventListener('touchmove', e => this.onDragMove(e.touches[0].clientX), { passive: true });
    window.addEventListener('touchend', () => this.onDragEnd());
  }

  onDragStart(x) {
    this.isDragging = true;
    this.lastX = x;
    this.wrap.style.cursor = 'grabbing';
    this.stopAutoplay();
  }

  onDragMove(x) {
    if (!this.isDragging || this.imageEls.length === 0) return;
    const delta = x - this.lastX;
    this.lastX = x;
    const step = this.container.getBoundingClientRect().width / (this.imageEls.length * this.sensitivity);
    if (Math.abs(delta) > step) {
      const dir = delta > 0 ? -1 : 1;
      this.currentIndex = (this.currentIndex + dir + this.imageEls.length) % this.imageEls.length;
      this.lastX = x;
      this.drawFrame();
    }
  }

  onDragEnd() {
    this.isDragging = false;
    this.wrap.style.cursor = 'grab';
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % Math.max(1, this.imageEls.length);
      this.drawFrame();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  destroy() {
    this.stopAutoplay();
    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.onDragEnd);
  }
}

/* --- 360 Modal Init --- */
document.addEventListener('DOMContentLoaded', () => {
  const btn360 = document.querySelector('.product-gallery__360-btn');
  const modal = document.querySelector('.viewer360-modal');
  const closeBtn = modal?.querySelector('.viewer360-close');
  const container = modal?.querySelector('.viewer360-container');

  if (!btn360 || !modal || !container) return;

  let viewer;

  btn360.addEventListener('click', () => {
    modal.classList.add('open');
    document.body.style.overflow = 'hidden';

    if (!viewer) {
      const rawImages = container.dataset.images;
      let images = [];
      try { images = JSON.parse(rawImages || '[]'); } catch {}

      if (images.length > 0) {
        viewer = new Viewer360({ images, container, autoplay: true });
      }
    } else {
      viewer.startAutoplay();
    }
  });

  const close = () => {
    modal.classList.remove('open');
    document.body.style.overflow = '';
    viewer?.stopAutoplay();
  };

  closeBtn?.addEventListener('click', close);
  modal.addEventListener('click', e => { if (e.target === modal) close(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
});
